<template>
  <div v-if="safeTotalPages > 1" class="cb-pagination-controls">
    <div class="cb-pagination-main">
      <span
        class="mr-2 cb-pagination-page cb-pagination-nav"
        :class="{ 'cb-pagination-page--disabled': isPreviousDisabled }"
        role="button"
        :tabindex="isPreviousDisabled ? -1 : 0"
        aria-label="Previous page"
        @click="goToPage(currentPage - 1)"
        @keydown.enter="goToPage(currentPage - 1)"
        @keydown.space.prevent="goToPage(currentPage - 1)"
      >
        <v-icon small>chevron_left</v-icon>
      </span>

    <template v-for="page in startPages">
      <span
        :key="'start-' + page"
        class="mx-1 cb-pagination-page"
        :class="{ 'cb-pagination-page--active': page === currentPage }"
        role="button"
        tabindex="0"
        @click="goToPage(page)"
        @keydown.enter="goToPage(page)"
        @keydown.space.prevent="goToPage(page)"
      >
        {{ page }}
      </span>
    </template>

    <span v-if="showMiddleSection && showLeftSeparator" class="mx-2 cb-pagination-separator">...</span>

    <template v-for="page in middlePagesAll">
      <span
        :key="'middle-' + page"
        class="mx-1 cb-pagination-page"
        :class="{ 'cb-pagination-page--active': page === currentPage }"
        role="button"
        tabindex="0"
        @click="goToPage(page)"
        @keydown.enter="goToPage(page)"
        @keydown.space.prevent="goToPage(page)"
      >
        {{ page }}
      </span>
    </template>

    <span v-if="showMiddleCurrentPage" class="mx-2 cb-pagination-page cb-pagination-page--active">{{ currentPage }}</span>

    <span v-if="showMiddleSection && showRightSeparator" class="mx-2 cb-pagination-separator">...</span>
    <span v-if="showCollapsedSeparator" class="mx-2 cb-pagination-separator">...</span>

    <template v-for="page in endPages">
      <span
        :key="'end-' + page"
        class="mx-1 cb-pagination-page"
        :class="{ 'cb-pagination-page--active': page === currentPage }"
        role="button"
        tabindex="0"
        @click="goToPage(page)"
        @keydown.enter="goToPage(page)"
        @keydown.space.prevent="goToPage(page)"
      >
        {{ page }}
      </span>
    </template>

      <span
        class="ml-2 cb-pagination-page cb-pagination-nav"
        :class="{ 'cb-pagination-page--disabled': isNextDisabled }"
        role="button"
        :tabindex="isNextDisabled ? -1 : 0"
        aria-label="Next page"
        @click="goToPage(currentPage + 1)"
        @keydown.enter="goToPage(currentPage + 1)"
        @keydown.space.prevent="goToPage(currentPage + 1)"
      >
        <v-icon small>chevron_right</v-icon>
      </span>
    </div>

    <v-select
      class="cb-pagination-jump-select"
      :items="allPages"
      v-model="jumpToPage"
      label="Jump to Page"
      hide-details
      solo
      flat
      @change="handleJumpToPage"
    ></v-select>
  </div>
</template>

<script>
export default {
  name: "CBPagination",
  props: {
    value: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      jumpToPage: null,
    }
  },
  computed: {
    currentPage() {
      return parseInt(this.value, 10)
    },
    groupSize() {
      return 3
    },
    twoGroupThreshold() {
      return this.groupSize * 2
    },
    leftSetSize() {
      return this.groupSize
    },
    rightSetSize() {
      return this.groupSize
    },
    safeTotalPages() {
      return this.totalPages > 0 ? this.totalPages : 1
    },
    startPages() {
      const last = Math.min(this.leftSetSize, this.safeTotalPages)
      const pages = []
      for (let page = 1; page <= last; page += 1) {
        pages.push(page)
      }
      return pages
    },
    showRightSet() {
      if (this.safeTotalPages <= this.leftSetSize) {
        return false
      }
      if (this.safeTotalPages <= this.twoGroupThreshold) {
        return this.safeTotalPages > this.leftSetSize
      }
      return true
    },
    endPages() {
      if (!this.showRightSet) {
        return []
      }
      const pages = []
      if (this.safeTotalPages <= this.twoGroupThreshold) {
        for (let page = this.leftSetSize + 1; page <= this.safeTotalPages; page += 1) {
          if (this.startPages.indexOf(page) === -1) {
            pages.push(page)
          }
        }
        return pages
      }
      const first = Math.max(this.safeTotalPages - (this.rightSetSize - 1), 1)
      for (let page = first; page <= this.safeTotalPages; page += 1) {
        if (this.startPages.indexOf(page) === -1) {
          pages.push(page)
        }
      }
      return pages
    },
    showMiddleSet() {
      return this.safeTotalPages > this.twoGroupThreshold
    },
    showMiddleCurrentPage() {
      if (!this.showMiddleSet) {
        return false
      }
      if (this.startPages.indexOf(this.currentPage) !== -1 || this.endPages.indexOf(this.currentPage) !== -1) {
        return false
      }
      if (this.middlePagesAll.indexOf(this.currentPage) !== -1) {
        return false
      }
      return true
    },
    showMiddleSection() {
      return this.showMiddleSet && this.middlePagesAll.length > 0
    },
    middlePagesAll() {
      if (!this.showMiddleSet) {
        return []
      }
      const lastStartPage = this.startPages[this.startPages.length - 1] || 0
      const firstEndPage = this.endPages.length > 0 ? this.endPages[0] : this.safeTotalPages + 1
      const self = this

      const rangePages = function (from, to) {
        const out = []
        for (let page = from; page <= to; page += 1) {
          out.push(page)
        }
        return out
      }

      const pickMiddleWindow = (current, availableFrom, availableTo) => {
        if (availableFrom > availableTo) {
          return []
        }
        const availableCount = availableTo - availableFrom + 1
        if (availableCount <= 0) {
          return []
        }
        if (availableCount <= 2) {
          return rangePages(availableFrom, availableTo)
        }
        let from = current - 1
        let to = current + 1
        if (from < availableFrom) {
          const shift = availableFrom - from
          from += shift
          to += shift
        }
        if (to > availableTo) {
          const shift = to - availableTo
          from -= shift
          to -= shift
        }
        from = Math.max(from, availableFrom)
        to = Math.min(to, availableTo)
        if (to - from + 1 < self.groupSize) {
          if (from > availableFrom) {
            from = Math.max(availableFrom, to - (self.groupSize - 1))
          } else if (to < availableTo) {
            to = Math.min(availableTo, from + (self.groupSize - 1))
          }
        }
        return rangePages(from, to)
      }

      const availableFrom = lastStartPage + 1
      const availableTo = firstEndPage - 1
      const baseWindow = pickMiddleWindow(this.currentPage, availableFrom, availableTo)
      const filtered = baseWindow.filter((page) => {
        return this.startPages.indexOf(page) === -1 && this.endPages.indexOf(page) === -1
      })
      const ensureCurrentInMiddle = function (pages) {
        const current = self.currentPage
        if (current < availableFrom || current > availableTo) {
          return pages
        }
        if (self.startPages.indexOf(current) !== -1 || self.endPages.indexOf(current) !== -1) {
          return pages
        }
        if (pages.indexOf(current) !== -1) {
          return pages
        }
        const merged = pages.concat([current])
        merged.sort((a, b) => {
          return a - b
        })
        return merged
      }

      if (filtered.length > 0) {
        return ensureCurrentInMiddle(filtered)
      }

      const bridgeTail = function () {
        const tailFrom = Math.max(availableFrom, firstEndPage - self.groupSize)
        const tailTo = Math.min(availableTo, firstEndPage - 1)
        return rangePages(tailFrom, tailTo).filter((page) => {
          return self.startPages.indexOf(page) === -1 && self.endPages.indexOf(page) === -1
        })
      }

      const bridgeHead = function () {
        const headFrom = Math.max(availableFrom, lastStartPage + 1)
        const headTo = Math.min(availableTo, lastStartPage + self.groupSize)
        return rangePages(headFrom, headTo).filter((page) => {
          return self.startPages.indexOf(page) === -1 && self.endPages.indexOf(page) === -1
        })
      }

      if (this.currentPage <= lastStartPage) {
        return ensureCurrentInMiddle(bridgeTail())
      }

      if (this.currentPage >= firstEndPage) {
        return ensureCurrentInMiddle(bridgeHead())
      }

      if (this.currentPage === lastStartPage + 1) {
        const tail = bridgeTail()
        if (tail.length > 0) {
          return ensureCurrentInMiddle(tail)
        }
      }

      if (this.currentPage === firstEndPage - 1) {
        const head = bridgeHead()
        if (head.length > 0) {
          return ensureCurrentInMiddle(head)
        }
      }

      const nearFrom = Math.max(availableFrom, this.currentPage - 1)
      const nearTo = Math.min(availableTo, this.currentPage + 1)
      return ensureCurrentInMiddle(
        rangePages(nearFrom, nearTo).filter((page) => {
          return this.startPages.indexOf(page) === -1 && this.endPages.indexOf(page) === -1
        })
      )
    },
    allPages() {
      const pages = []
      for (let page = 1; page <= this.safeTotalPages; page += 1) {
        pages.push(page)
      }
      return pages
    },
    showLeftSeparator() {
      if (!this.showMiddleSection || this.startPages.length === 0 || this.middlePagesAll.length === 0) {
        return false
      }
      const lastStartPage = this.startPages[this.startPages.length - 1] || 0
      const firstMiddlePage = this.middlePagesAll[0]
      return firstMiddlePage > lastStartPage + 1
    },
    showRightSeparator() {
      if (!this.showMiddleSection || this.endPages.length === 0 || this.middlePagesAll.length === 0) {
        return false
      }
      const firstEndPage = this.endPages[0] || this.safeTotalPages + 1
      const lastMiddlePage = this.middlePagesAll[this.middlePagesAll.length - 1]
      return firstEndPage > lastMiddlePage + 1
    },
    showCollapsedSeparator() {
      if (this.showMiddleSection || this.endPages.length === 0 || this.startPages.length === 0) {
        return false
      }
      const lastStartPage = this.startPages[this.startPages.length - 1] || 0
      const firstEndPage = this.endPages[0] || this.safeTotalPages + 1
      return firstEndPage > lastStartPage + 1
    },
    isPreviousDisabled() {
      return this.disabled || this.currentPage <= 1
    },
    isNextDisabled() {
      return this.disabled || this.currentPage >= this.safeTotalPages
    },
  },
  methods: {
    goToPage(page) {
      const numericPage = parseInt(page, 10)
      if (!Number.isFinite(numericPage)) {
        return
      }
      const nextPage = Math.min(Math.max(numericPage, 1), this.safeTotalPages)
      if (nextPage !== this.currentPage) {
        this.$emit("input", nextPage)
      }
    },
    handleJumpToPage(page) {
      this.goToPage(page)
      this.jumpToPage = null
    },
  },
}
</script>

<style scoped>
.cb-pagination-controls {
  display: flex;
  align-items: center;
  width: 100%;
}

.cb-pagination-main {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  flex-wrap: wrap;
}

.cb-pagination-jump-select {
  max-width: 140px;
  margin-left: 12px;
}

.cb-pagination-separator {
  font-weight: 700;
  color: #666666;
}

.cb-pagination-page {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 9px;
  border-radius: 4px;
  color: #506176;
  cursor: pointer;
  user-select: none;
}

.cb-pagination-page:hover {
  background: rgba(80, 97, 118, 0.1);
}

.cb-pagination-page:focus {
  outline: 2px solid rgba(38, 136, 227, 0.5);
  outline-offset: 1px;
}

.cb-pagination-page--active {
  color: #ffffff;
  background: #1d303e;
}

.cb-pagination-page--active:hover {
  background: #1d303e;
}

.cb-pagination-page--disabled {
  opacity: 0.35;
  cursor: default;
}

.cb-pagination-page--disabled:hover {
  background: transparent;
}

</style>
